import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  IconButton,
  Tooltip,
  Stack,
  Divider,
  Tab,
  Tabs,
  LinearProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ArrowBack as ArrowBackIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  Link as LinkIcon,
  Analytics as AnalyticsIcon,
  Star as StarIcon,
  Public as PublicIcon,
  Timeline as TimelineIcon,
  EmojiEvents as EmojiEventsIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import SerpVisualization from '../components/shared/SerpVisualization';

interface RankingSummary {
  metadata: {
    generated_at: string;
    total_queries: number;
    successful_queries: number;
    tracked_domains: string[];
    num_results_per_query: number;
  };
  overview: {
    queries_with_rankings: number;
    total_rankings_found: number;
    average_position?: number;
    best_ranking?: number;
    worst_ranking?: number;
  };
  domain_summary: Record<string, {
    total_appearances: number;
    queries_found: string[];
    positions: number[];
    average_position?: number;
    best_position?: number;
    worst_position?: number;
    result_types: { organic: number; local: number };
  }>;
  query_rankings: Array<{
    query: string;
    timestamp: string;
    domain_rankings: Record<string, {
      position: number;
      title: string;
      link: string;
      result_type: string;
    }>;
    organic_results_count: number;
    local_results_count: number;
    total_results: number;
    raw_html_file: string;
    full_serp_data?: any; // Complete SERP response for exploration
  }>;
}

interface MarketIntelligence {
  metadata: {
    generated_at: string;
    total_queries_analyzed: number;
    queries_with_local_results: number;
    total_businesses_found: number;
  };
  markets: Record<string, {
    location: string;
    businesses_found: number;
    avg_rating: number;
    twenty_four_seven_count: number;
    emergency_services_count: number;
    family_owned_count: number;
    competition_level: string;
    local_ads_count: number;
    businesses: Array<{
      title: string;
      rating: number;
      reviews: number;
      is_24_7: boolean;
      is_emergency: boolean;
      is_family_owned: boolean;
      score: number;
    }>;
  }>;
  insights: {
    total_markets: number;
    highest_rated_market?: {
      location: string;
      avg_rating: number;
      business_count: number;
    };
    most_competitive_market?: {
      location: string;
      business_count: number;
      local_ads: number;
      competition_level: string;
    };
    service_trends: {
      emergency_services: number;
      twenty_four_seven: number;
      family_owned: number;
    };
  };
  top_performers: Array<{
    rank: number;
    title: string;
    market: string;
    rating: number;
    reviews: number;
    score: number;
    is_24_7: boolean;
    is_emergency: boolean;
    is_family_owned: boolean;
  }>;
  market_comparison: {
    by_business_count: Array<{ location: string; count: number }>;
    by_rating: Array<{ location: string; rating: number; competition: number }>;
    by_competition: Array<{ location: string; level: string; count: number }>;
  };
}

const InteractiveReport: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [rankingSummary, setRankingSummary] = useState<RankingSummary | null>(null);
  const [marketIntelligence, setMarketIntelligence] = useState<MarketIntelligence | null>(null);
  const [loading, setLoading] = useState(true);
  const [marketIntelLoading, setMarketIntelLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [positionFilter, setPositionFilter] = useState<string>('all');
  const [resultTypeFilter, setResultTypeFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState(0);

  // Check if data was passed via navigation state
  const passedData = location.state?.rankingSummary as RankingSummary | undefined;
  const passedResultData = location.state?.resultData as any;

  useEffect(() => {
    if (passedData) {
      setRankingSummary(passedData);
      setLoading(false);
      
      // Try to fetch market intelligence if we have result data
      if (passedResultData?.download_urls?.market_intelligence) {
        fetchMarketIntelligence(passedResultData.download_urls.market_intelligence);
      }
    } else if (passedResultData) {
      // Handle case where we have result data but no ranking summary
      // Create a basic summary structure for display
      const basicSummary: RankingSummary = {
        metadata: {
          generated_at: new Date().toISOString(),
          total_queries: passedResultData.total_queries || 0,
          successful_queries: passedResultData.success_count || 0,
          tracked_domains: [],
          num_results_per_query: 20 // default
        },
        overview: {
          queries_with_rankings: 0,
          total_rankings_found: 0,
          average_position: undefined,
          best_ranking: undefined,
          worst_ranking: undefined
        },
        domain_summary: {},
        query_rankings: []
      };
      setRankingSummary(basicSummary);
      setLoading(false);
      
      // Try to fetch market intelligence
      if (passedResultData?.download_urls?.market_intelligence) {
        fetchMarketIntelligence(passedResultData.download_urls.market_intelligence);
      }
    } else if (batchId) {
      // If no data passed, we'd need to fetch it by batch ID
      // For now, show an error since we don't have the download URL
      setError('No ranking data available. Please generate a new report from the SERP tool.');
      setLoading(false);
    } else {
      setError('No batch ID or data provided.');
      setLoading(false);
    }
  }, [batchId, passedData, passedResultData]);

  const fetchMarketIntelligence = async (url: string) => {
    setMarketIntelLoading(true);
    try {
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setMarketIntelligence(data);
      } else {
        console.warn('Failed to fetch market intelligence data');
      }
    } catch (err) {
      console.error('Error fetching market intelligence:', err);
    } finally {
      setMarketIntelLoading(false);
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      alert('Report link copied to clipboard!');
    });
  };

  const getPositionColor = (position: number) => {
    if (position <= 3) return 'success';
    if (position <= 10) return 'warning';
    return 'default';
  };

  const getCompetitionColor = (level: string) => {
    switch (level) {
      case 'High': return 'error';
      case 'Medium': return 'warning';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  const MarketIntelligenceSection = () => {
    if (!marketIntelligence) {
      return (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AnalyticsIcon color="primary" />
              Market Intelligence Dashboard
            </Typography>
            {marketIntelLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
                <CircularProgress size={24} />
                <Typography>Loading market intelligence data...</Typography>
              </Box>
            ) : (
              <Alert severity="info">
                Market intelligence data is not available for this batch. 
                Local results with business data are required for market analysis.
              </Alert>
            )}
          </CardContent>
        </Card>
      );
    }

    const markets = Object.values(marketIntelligence.markets);

    return (
      <Box>
        {/* Market Intelligence Overview */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AnalyticsIcon color="primary" />
              Market Intelligence Dashboard
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="primary">
                    {marketIntelligence.insights.total_markets}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Markets Analyzed
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="info.main">
                    {marketIntelligence.metadata.total_businesses_found}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Businesses Found
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="success.main">
                    {marketIntelligence.insights.highest_rated_market?.avg_rating?.toFixed(1) || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Highest Avg Rating
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="warning.main">
                    {marketIntelligence.insights.service_trends.twenty_four_seven}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    24/7 Services
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Cross-Market Insights Cards */}
            <Grid container spacing={2}>
              {marketIntelligence.insights.highest_rated_market && (
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <StarIcon color="success" />
                        <Typography variant="subtitle2">Highest-Rated Market</Typography>
                      </Box>
                      <Typography variant="h6">
                        {marketIntelligence.insights.highest_rated_market.location}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {marketIntelligence.insights.highest_rated_market.avg_rating.toFixed(1)} ★ average 
                        ({marketIntelligence.insights.highest_rated_market.business_count} businesses)
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              
              {marketIntelligence.insights.most_competitive_market && (
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <TrendingUpIcon color="error" />
                        <Typography variant="subtitle2">Most Competitive</Typography>
                      </Box>
                      <Typography variant="h6">
                        {marketIntelligence.insights.most_competitive_market.location}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {marketIntelligence.insights.most_competitive_market.business_count} businesses, 
                        {marketIntelligence.insights.most_competitive_market.local_ads} ads
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <BusinessIcon color="info" />
                      <Typography variant="subtitle2">Service Trends</Typography>
                    </Box>
                    <Typography variant="body2">
                      Emergency: {marketIntelligence.insights.service_trends.emergency_services}
                    </Typography>
                    <Typography variant="body2">
                      24/7: {marketIntelligence.insights.service_trends.twenty_four_seven}
                    </Typography>
                    <Typography variant="body2">
                      Family-owned: {marketIntelligence.insights.service_trends.family_owned}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Multi-Market Overview Table */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PublicIcon color="primary" />
              Multi-Market Overview
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Location</TableCell>
                    <TableCell align="center">Businesses Found</TableCell>
                    <TableCell align="center">Avg Rating</TableCell>
                    <TableCell align="center">24/7 Services %</TableCell>
                    <TableCell align="center">Emergency Services %</TableCell>
                    <TableCell align="center">Competition Level</TableCell>
                    <TableCell align="center">Local Ads</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {markets.map((market) => (
                    <TableRow key={market.location} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationOnIcon color="action" fontSize="small" />
                          <Typography variant="subtitle2">{market.location}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={market.businesses_found} size="small" />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <Typography variant="body2">
                            {market.avg_rating > 0 ? market.avg_rating.toFixed(1) : 'N/A'}
                          </Typography>
                          {market.avg_rating > 0 && <StarIcon fontSize="small" color="warning" />}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {market.businesses_found > 0 ? 
                          Math.round((market.twenty_four_seven_count / market.businesses_found) * 100) : 0}%
                      </TableCell>
                      <TableCell align="center">
                        {market.businesses_found > 0 ? 
                          Math.round((market.emergency_services_count / market.businesses_found) * 100) : 0}%
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={market.competition_level}
                          size="small"
                          color={getCompetitionColor(market.competition_level)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {market.local_ads_count}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Top Performers Ranking */}
        {marketIntelligence.top_performers.length > 0 && (
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmojiEventsIcon color="primary" />
                Top Performers Across All Markets
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">Rank</TableCell>
                      <TableCell>Business</TableCell>
                      <TableCell>Market</TableCell>
                      <TableCell align="center">Rating</TableCell>
                      <TableCell align="center">Reviews</TableCell>
                      <TableCell align="center">Score</TableCell>
                      <TableCell align="center">Services</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {marketIntelligence.top_performers.slice(0, 10).map((performer) => (
                      <TableRow key={performer.rank} hover>
                        <TableCell align="center">
                          <Chip 
                            label={`#${performer.rank}`}
                            size="small"
                            color={performer.rank <= 3 ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2">{performer.title}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {performer.market}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <Typography variant="body2">{performer.rating.toFixed(1)}</Typography>
                            <StarIcon fontSize="small" color="warning" />
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">{performer.reviews}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color="primary">
                            {performer.score.toFixed(1)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            {performer.is_24_7 && (
                              <Chip label="24/7" size="small" variant="outlined" color="info" />
                            )}
                            {performer.is_emergency && (
                              <Chip label="Emergency" size="small" variant="outlined" color="error" />
                            )}
                            {performer.is_family_owned && (
                              <Chip label="Family" size="small" variant="outlined" color="success" />
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
      </Box>
    );
  };

  const filteredQueries = rankingSummary?.query_rankings.filter(query => {
    const matchesSearch = searchQuery === '' || 
      query.query.toLowerCase().includes(searchQuery.toLowerCase());
    
    // If no domains are tracked, just filter by search
    if (rankingSummary.metadata.tracked_domains.length === 0) {
      return matchesSearch;
    }
    
    const hasRelevantRankings = selectedDomain === 'all' || 
      Object.keys(query.domain_rankings || {}).includes(selectedDomain);
    
    if (!hasRelevantRankings) return false;

    if (selectedDomain !== 'all') {
      const domainRanking = query.domain_rankings?.[selectedDomain];
      if (!domainRanking) return false;
      
      const matchesPosition = positionFilter === 'all' ||
        (positionFilter === 'top3' && domainRanking.position <= 3) ||
        (positionFilter === 'top10' && domainRanking.position <= 10) ||
        (positionFilter === 'beyond10' && domainRanking.position > 10);
      
      const matchesType = resultTypeFilter === 'all' ||
        domainRanking.result_type === resultTypeFilter;
      
      return matchesSearch && matchesPosition && matchesType;
    }
    
    return matchesSearch;
  }) || [];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !rankingSummary) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Failed to load ranking data'}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/serp-tool')}>
          Back to SERP Tool
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/serp-tool')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1">
              Interactive Ranking Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Generated: {new Date(rankingSummary.metadata.generated_at).toLocaleString()}
              {batchId && ` | Batch: ${batchId}`}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Share Report">
            <IconButton onClick={handleShare}>
              <ShareIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Tabs for different sections */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Performance Overview" />
          <Tab label="Market Intelligence" />
          <Tab label="Detailed Rankings" />
          <Tab 
            label="SERP Visualization" 
            icon={<VisibilityIcon />}
            disabled={!rankingSummary?.query_rankings?.some(q => q.raw_html_file)}
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Box>
          {/* Overview Stats */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssessmentIcon color="primary" />
                    {rankingSummary.metadata.tracked_domains.length > 0 ? 'Performance Overview' : 'SERP Batch Overview'}
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={6} md={3}>
                      <Box textAlign="center">
                        <Typography variant="h4" color="primary">
                          {rankingSummary.metadata.successful_queries}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Successful Queries
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box textAlign="center">
                        <Typography variant="h4" color="info.main">
                          {rankingSummary.metadata.total_queries}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Queries
                        </Typography>
                      </Box>
                    </Grid>
                    {rankingSummary.metadata.tracked_domains.length > 0 ? (
                      <>
                        <Grid item xs={6} md={3}>
                          <Box textAlign="center">
                            <Typography variant="h4" color="success.main">
                              {rankingSummary.overview.best_ranking || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Best Position
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Box textAlign="center">
                            <Typography variant="h4" color="text.primary">
                              {rankingSummary.overview.total_rankings_found}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Total Rankings
                            </Typography>
                          </Box>
                        </Grid>
                      </>
                    ) : (
                      <>
                        <Grid item xs={6} md={3}>
                          <Box textAlign="center">
                            <Typography variant="h4" color="warning.main">
                              {passedResultData?.failed_count || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Failed Queries
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Box textAlign="center">
                            <Typography variant="h4" color="success.main">
                              {passedResultData?.processing_time_seconds?.toFixed(1) || 'N/A'}s
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Processing Time
                            </Typography>
                          </Box>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon color="secondary" />
                    {rankingSummary.metadata.tracked_domains.length > 0 ? 'Tracked Domains' : 'Batch Information'}
                  </Typography>
                  {rankingSummary.metadata.tracked_domains.length > 0 ? (
                    <Stack spacing={1}>
                      {rankingSummary.metadata.tracked_domains.map((domain, index) => (
                        <Chip 
                          key={index}
                          label={domain}
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Batch ID: {batchId || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Results per Query: {rankingSummary.metadata.num_results_per_query}
                      </Typography>
                      <Alert severity="info" sx={{ mt: 2 }}>
                        No domains were tracked in this batch. Add domains in the SERP tool for ranking analysis.
                      </Alert>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Domain Performance Summary */}
          {rankingSummary.metadata.tracked_domains.length > 0 ? (
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon color="primary" />
                  Domain Performance Summary
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Domain</TableCell>
                        <TableCell align="center">Total Appearances</TableCell>
                        <TableCell align="center">Average Position</TableCell>
                        <TableCell align="center">Best Position</TableCell>
                        <TableCell align="center">Worst Position</TableCell>
                        <TableCell align="center">Organic Results</TableCell>
                        <TableCell align="center">Local Results</TableCell>
                        <TableCell align="center">Performance</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(rankingSummary.domain_summary).map(([domain, stats]) => (
                        <TableRow key={domain} hover>
                          <TableCell>
                            <Typography variant="subtitle2">{domain}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={stats.total_appearances} size="small" />
                          </TableCell>
                          <TableCell align="center">
                            {stats.average_position && (
                              <Chip 
                                label={stats.average_position}
                                size="small"
                                color={getPositionColor(stats.average_position)}
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {stats.best_position && (
                              <Chip 
                                label={stats.best_position}
                                size="small"
                                color="success"
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {stats.worst_position && (
                              <Chip 
                                label={stats.worst_position}
                                size="small"
                                color="default"
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">{stats.result_types.organic}</TableCell>
                          <TableCell align="center">{stats.result_types.local}</TableCell>
                          <TableCell align="center">
                            {stats.best_position && stats.best_position <= 3 && (
                              <Chip label="Excellent" color="success" size="small" />
                            )}
                            {stats.best_position && stats.best_position > 3 && stats.best_position <= 10 && (
                              <Chip label="Good" color="warning" size="small" />
                            )}
                            {stats.best_position && stats.best_position > 10 && (
                              <Chip label="Needs Work" color="default" size="small" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          ) : (
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon color="primary" />
                  SERP Data Available
                </Typography>
                <Alert severity="info">
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    This batch contains SERP data without domain-specific ranking analysis.
                  </Typography>
                  <Typography variant="body2">
                    To get detailed ranking analysis and performance metrics, add domains to track when running your next SERP batch in the SERP Tool.
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {activeTab === 1 && <MarketIntelligenceSection />}

      {activeTab === 2 && (
        <Box>
          {/* Filters */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Filter Results
              </Typography>
              <Grid container spacing={2}>
                {rankingSummary.metadata.tracked_domains.length > 0 && (
                  <>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Domain</InputLabel>
                        <Select
                          value={selectedDomain}
                          onChange={(e) => setSelectedDomain(e.target.value)}
                          label="Domain"
                        >
                          <MenuItem value="all">All Domains</MenuItem>
                          {rankingSummary.metadata.tracked_domains.map((domain) => (
                            <MenuItem key={domain} value={domain}>
                              {domain}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Position Range</InputLabel>
                        <Select
                          value={positionFilter}
                          onChange={(e) => setPositionFilter(e.target.value)}
                          label="Position Range"
                        >
                          <MenuItem value="all">All Positions</MenuItem>
                          <MenuItem value="top3">Top 3</MenuItem>
                          <MenuItem value="top10">Top 10</MenuItem>
                          <MenuItem value="beyond10">Beyond 10</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Result Type</InputLabel>
                        <Select
                          value={resultTypeFilter}
                          onChange={(e) => setResultTypeFilter(e.target.value)}
                          label="Result Type"
                        >
                          <MenuItem value="all">All Types</MenuItem>
                          <MenuItem value="organic">Organic</MenuItem>
                          <MenuItem value="local">Local</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search queries"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
                    }}
                  />
                </Grid>
              </Grid>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Showing {filteredQueries.length} of {rankingSummary.query_rankings.length} queries
              </Typography>
            </CardContent>
          </Card>

          {/* Query Rankings Table */}
          {filteredQueries.length > 0 ? (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SearchIcon color="primary" />
                  Query Rankings {selectedDomain !== 'all' && `for ${selectedDomain}`}
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Query</TableCell>
                        <TableCell align="center">Timestamp</TableCell>
                        {rankingSummary.metadata.tracked_domains.length > 0 ? (
                          <>
                            <TableCell align="center">Position</TableCell>
                            <TableCell align="center">Result Type</TableCell>
                            <TableCell>Title</TableCell>
                            <TableCell align="center">Link</TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell align="center">Organic Results</TableCell>
                            <TableCell align="center">Local Results</TableCell>
                            <TableCell align="center">Total Results</TableCell>
                          </>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredQueries.map((query, index) => {
                        if (rankingSummary.metadata.tracked_domains.length > 0) {
                          // Show domain rankings
                          const rankings = selectedDomain === 'all' 
                            ? Object.entries(query.domain_rankings || {})
                            : [[selectedDomain, query.domain_rankings?.[selectedDomain]]].filter(([_, ranking]) => ranking);
                          
                          return rankings.map(([domain, ranking]) => {
                            if (!ranking) return null;
                            
                            return (
                              <TableRow key={`${index}-${domain}`} hover>
                                <TableCell>
                                  <Typography variant="body2">{query.query}</Typography>
                                  {selectedDomain === 'all' && (
                                    <Chip label={domain} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                                  )}
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2" color="text.secondary">
                                    {new Date(query.timestamp).toLocaleTimeString()}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Chip 
                                    label={ranking.position}
                                    size="small"
                                    color={getPositionColor(ranking.position)}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Chip 
                                    label={ranking.result_type}
                                    size="small"
                                    variant="outlined"
                                    color={ranking.result_type === 'local' ? 'info' : 'default'}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {ranking.title}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  {ranking.link && (
                                    <Tooltip title={ranking.link}>
                                      <IconButton 
                                        size="small" 
                                        onClick={() => window.open(ranking.link, '_blank')}
                                      >
                                        <LinkIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          }).filter(Boolean);
                        } else {
                          // Show query information without rankings
                          return (
                            <TableRow key={index} hover>
                              <TableCell>
                                <Typography variant="body2">{query.query}</Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2" color="text.secondary">
                                  {new Date(query.timestamp).toLocaleTimeString()}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip label={query.organic_results_count} size="small" />
                              </TableCell>
                              <TableCell align="center">
                                <Chip label={query.local_results_count} size="small" color="info" />
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2" color="text.secondary">
                                  {query.total_results.toLocaleString()}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        }
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <Alert severity="info">
                  No queries match your current filter criteria. Try adjusting the filters above.
                </Alert>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {activeTab === 3 && (
        <Box>
          <SerpVisualization
            queries={rankingSummary.query_rankings.filter(q => q.raw_html_file)}
            selectedQuery={searchQuery || undefined}
            onQueryChange={(query) => setSearchQuery(query)}
          />
        </Box>
      )}
    </Box>
  );
};

export default InteractiveReport; 